/**
 * A compact single-date trigger field: shows the selected date as `dd.MM.yyyy`
 * (R18.10) in a small input-like control, and opens the existing {@link
 * InlineDateRangePicker} as a popover on click — the same UI/UX as {@link
 * DateRangeField}, for the places a screen needs exactly one date rather than a
 * range (a milestone's planned/completed date, a dependency's due date, a
 * master-data record's effective-from, etc.).
 *
 * ## Reusing a range picker for one date
 *
 * `InlineDateRangePicker` has no single-date mode of its own, and its click
 * rules are not touched here (per the same reuse constraint `DateRangeField`
 * follows): a first click on an empty selection already lands on exactly the
 * single-day case this field wants (`{ startDate: day, endDate: day }`), and
 * clicking the same day again already clears it. The one rule that does not
 * fit a single-date field is what happens on a *second* click after a day is
 * already selected — the picker's own rule turns that into a two-day range,
 * because that is what a range field needs. This field intercepts exactly that
 * case: whichever endpoint of the resulting range is *not* the day already
 * selected is the day the user just clicked, and that becomes the new single
 * date, discarding the range the picker computed for its own purposes. No
 * click, hover, keyboard or clear behaviour inside the calendar is
 * reimplemented — only the day-selected event this field receives from it is
 * reinterpreted.
 */

import { useState } from 'react';
import { Popover } from 'radix-ui';
import { useTranslation } from 'react-i18next';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate } from '@/shared/format/date-format';
import { InlineDateRangePicker, type DateRange } from './InlineDateRangePicker';

export interface DateFieldProps {
  /** Current selected date (ISO `YYYY-MM-DD`), or `null`/`undefined` for none. */
  readonly value?: string | null;
  /** Callback when the date changes. */
  readonly onChange: (date: string | null) => void;
  /** Initial month the popover's calendar opens on. */
  readonly initialMonth?: Date;
  /** Minimum selectable date (ISO string). */
  readonly minDate?: string;
  /** Maximum selectable date (ISO string). */
  readonly maxDate?: string;
  /** The accessible name for the trigger button; caller supplies, already resolved (R18.9). */
  readonly label: string;
  /** Additional CSS classes for the trigger button. */
  readonly className?: string;
}

export function DateField({
  value,
  onChange,
  initialMonth,
  minDate,
  maxDate,
  label,
  className,
}: DateFieldProps): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // "No date" is null, undefined *or* a blank string, and the blank string is not a
  // hypothetical: a new milestone row is appended with `plannedDate: ''`, and the
  // master-data and effort-assumption dialogs write `''` back when their date is
  // cleared. Only the picker value used to allow for that (a truthiness check); the
  // display path tested `== null`, which a blank string passes straight through to
  // `formatDate` — whose parser throws on a non-ISO value by design rather than
  // rendering something wrong. Normalising once here keeps the two paths agreeing
  // and leaves that deliberate strictness intact.
  const current = typeof value === 'string' && value.trim().length > 0 ? value : null;
  const pickerValue: DateRange | null =
    current === null ? null : { startDate: current, endDate: current };

  const displayText = current === null ? t('datePicker.rangePlaceholder') : formatDate(current);

  // See this module's doc comment: collapse whatever the range picker computed
  // back to the single day the click actually meant, then close — a single-date
  // field's selection is always complete the moment a day resolves.
  const handlePickerChange = (range: DateRange | null): void => {
    if (range === null) {
      onChange(null);
      return;
    }
    const nextDate = range.startDate === range.endDate
      ? range.startDate
      : range.startDate === current
        ? range.endDate
        : range.startDate;
    onChange(nextDate);
    setOpen(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          className={cn(
            'border-line bg-panel inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-control)] border px-2 text-[12.5px] text-txt outline-none focus-visible:border-[var(--accent)]',
            current === null && 'text-txt-subtle',
            className,
          )}
        >
          <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-txt-subtle" aria-hidden="true" />
          <span className="whitespace-nowrap">{displayText}</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="start" sideOffset={6} className="z-50">
          <InlineDateRangePicker
            value={pickerValue}
            onChange={handlePickerChange}
            {...(initialMonth ? { initialMonth } : {})}
            {...(minDate !== undefined ? { minDate } : {})}
            {...(maxDate !== undefined ? { maxDate } : {})}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
